// src/pages/StaffManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Cake as CakeIcon } from '@mui/icons-material';

// Set this to the base URL of your PHP backend where staff_management.php is located.
const API_URL = "https://wg.pizzahub.lk/api/staff_management.php";

const salaryTermsOptions = [
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Half Month", value: "half_month" },
  { label: "Custom", value: "custom" },
  { label: "Daily", value: "daily" },
];

const roleOptions = [
  { label: "Waiter", value: "waiter" },
  { label: "Chef", value: "chef" },
  { label: "Manager", value: "manager" },
  { label: "Bartender", value: "bartender" },
  // Add other restaurant roles as needed.
];

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const StaffManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Extend initial state with additional fields: phone, address, date_of_birth.
  const [staff, setStaff] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [staffData, setStaffData] = useState({
    id: null,
    name: '',
    role: '',
    phone: '',
    address: '',
    date_of_birth: '', // Expected format "YYYY-MM-DD"
    basic_salary: '',
    salary_terms: '',
    active_status: '',
  });
  const [wishLoading, setWishLoading] = useState(false);

  // Fetch all staff records.
  const fetchStaff = () => {
    fetch(API_URL)
      .then((res) => res.json())
      .then((data) => {
        setStaff(data);
      })
      .catch((error) => console.error('Error fetching staff:', error));
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  // ─── Send Birthday Wishes ─────────────────────────────
  const handleBirthdayWishes = () => {
     if (!window.confirm("Send birthday wishes to everyone whose birthday is today?")) return;
     setWishLoading(true);
     fetch("https://wg.pizzahub.lk/api/send_birthday_wishes.php", {
       method: 'POST'
     })
       .then(r => r.json())
       .then(resp => {
         if (resp.status === 'success') {
           alert(`Sent ${resp.count} birthday wish${resp.count !== 1 ? 'es' : ''}!`);
         } else {
           alert("Error: "+resp.message);
         }
       })
       .catch(() => alert("Network error"))
       .finally(() => setWishLoading(false));
   };
  // Open modal for add or edit.
  const handleOpenModal = (member = null) => {
    if (member) {
      setStaffData(member);
      setIsEditMode(true);
    } else {
      setStaffData({
        id: null,
        name: '',
        role: '',
        phone: '',
        address: '',
        date_of_birth: '',
        basic_salary: '',
        salary_terms: '',
        active_status: '',
      });
      setIsEditMode(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setStaffData({
      id: null,
      name: '',
      role: '',
      phone: '',
      address: '',
      date_of_birth: '',
      basic_salary: '',
      salary_terms: '',
      active_status: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStaffData({ ...staffData, [name]: value });
  };

  // Save a staff record (add or update) based on the mode.
  const handleSave = () => {
    const action = isEditMode ? 'update' : 'add';
    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...staffData }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 'success') {
          fetchStaff();
          handleCloseModal();
        } else {
          console.error('Error saving staff:', data.message);
        }
      })
      .catch((error) => console.error('Error:', error));
  };

  // Delete a staff record.
  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete', id }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 'success') {
          fetchStaff();
        } else {
          console.error('Error deleting staff:', data.message);
        }
      })
      .catch((error) => console.error('Error:', error));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Typography variant="h4" sx={{ flexGrow: 1 }}>
      Staff Management
      </Typography>
      <Button
         startIcon={<CakeIcon />}
         variant="contained"
         color="secondary"
         onClick={handleBirthdayWishes}
         disabled={wishLoading}
       >
         {wishLoading ? 'Sending…' : 'Birthday Wishes'}
       </Button>
      </Box>
      <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
        Add Staff
      </Button>

      {/* Conditionally render view */}
      {isMobile ? (
        // Card view for mobile
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {staff.length > 0 ? (
            staff.map((member) => (
              <Card key={member.id} variant="outlined">
                <CardContent>
                  <Typography variant="h6">{member.name}</Typography>
                  <Typography variant="body2">
                    <strong>Role:</strong> {member.role}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phone:</strong> {member.phone}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Address:</strong> {member.address}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date of Birth:</strong> {member.date_of_birth}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Basic Salary:</strong> {member.basic_salary}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Salary Terms:</strong> {member.salary_terms}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {member.active_status}
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton onClick={() => handleOpenModal(member)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(member.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            ))
          ) : (
            <Typography align="center" variant="body1">
              No staff records found.
            </Typography>
          )}
        </Box>
      ) : (
        // Table view for larger screens
        <Paper sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Date of Birth</TableCell>
                <TableCell>Basic Salary</TableCell>
                <TableCell>Salary Terms</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staff.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>{member.name}</TableCell>
                  <TableCell>{member.role}</TableCell>
                  <TableCell>{member.phone}</TableCell>
                  <TableCell>{member.address}</TableCell>
                  <TableCell>{member.date_of_birth}</TableCell>
                  <TableCell>{member.basic_salary}</TableCell>
                  <TableCell>{member.salary_terms}</TableCell>
                  <TableCell>{member.active_status}</TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => handleOpenModal(member)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(member.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {staff.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No staff records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Add / Edit Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth>
        <DialogTitle>{isEditMode ? "Edit Staff" : "Add New Staff"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Name"
            name="name"
            value={staffData.name}
            onChange={handleInputChange}
            variant="standard"
          />

          <TextField
            select
            fullWidth
            margin="dense"
            label="Role"
            name="role"
            value={staffData.role}
            onChange={handleInputChange}
            variant="standard"
          >
            {roleOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            margin="dense"
            label="Phone"
            name="phone"
            value={staffData.phone}
            onChange={handleInputChange}
            variant="standard"
          />

          <TextField
            fullWidth
            margin="dense"
            label="Address"
            name="address"
            value={staffData.address}
            onChange={handleInputChange}
            variant="standard"
            multiline
            rows={2}
          />

          <TextField
            fullWidth
            margin="dense"
            label="Date of Birth"
            name="date_of_birth"
            type="date"
            value={staffData.date_of_birth}
            onChange={handleInputChange}
            variant="standard"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            fullWidth
            margin="dense"
            label="Basic Salary"
            name="basic_salary"
            type="number"
            value={staffData.basic_salary}
            onChange={handleInputChange}
            variant="standard"
          />

          <TextField
            select
            fullWidth
            margin="dense"
            label="Salary Terms"
            name="salary_terms"
            value={staffData.salary_terms}
            onChange={handleInputChange}
            variant="standard"
          >
            {salaryTermsOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            margin="dense"
            label="Status"
            name="active_status"
            value={staffData.active_status}
            onChange={handleInputChange}
            variant="standard"
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffManagement;
